import Dropzone, { DropzoneOptions } from 'react-dropzone';
import { Component, PropsWithChildren } from 'react';
import { DropzoneIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { PoolTypography } from '../typography/pool-typography';

// https://kovart.github.io/dashed-border-generator/ — the stroke colour is
// interpolated so the frame follows the theme instead of a fixed blue.
const dashedBorder = (stroke: string, width: number, dash: string) =>
    `data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='${encodeURIComponent(
        stroke
    )}' stroke-width='${width}' stroke-dasharray='${dash}' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e`;

const styles = (theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            // Centred rather than pinned to the top and the bottom: the zone
            // grows with the row it sits in, and icon and caption used to drift
            // apart as it did.
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1.5),
            flex: 1,
            minHeight: '144px', // screendesign
            backgroundImage: `url("${dashedBorder(theme.palette.divider, 1.5, '6%2c 6')}")`,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 4,
            padding: theme.spacing(2),
            cursor: 'pointer',

            '& svg': {
                width: 57,
                height: 50,
            },

            '&:hover, &:focus': {
                backgroundImage: `url("${dashedBorder(theme.palette.primary.main, 2, '0')}")`,
            },
        },
        disabled: {
            cursor: 'not-allowed',
            backgroundColor: theme.palette.background.default,

            '& svg': {
                color: theme.palette.text.disabled,
            },
            '&:hover': {
                backgroundImage: `url("${dashedBorder(theme.palette.divider, 1.5, '6%2c 6')}")`,
            },
        },
        caption: {
            color: theme.palette.text.secondary,
            width: '100%',
            textAlign: 'center',
            paddingLeft: 'inherit',
            paddingRight: 'inherit',
        },
        onAccept: {
            backgroundImage: `url("${dashedBorder(theme.palette.primary.main, 2, '0')}")`,
            backgroundColor: theme.palette.background.default,
        },
        onReject: {},
        fileList: {
            padding: 0,
            margin: 0,
            listStyle: 'none',
            color: theme.palette.primary.main,

            '& li + li': {
                marginTop: theme.spacing(),
            },

            '& svg': {
                marginRight: theme.spacing(),
            },
        },
        fileContainer: {
            display: 'inline-flex',
            alignItems: 'center',
        },
    });

interface PoolDropzoneProps extends DropzoneOptions, WithStyles<typeof styles> {
    acceptedFiles?: File[];
    className?: string;
}

// HINT: accept with MIME type is not working for Chrome with Ubuntu as of Feb. 2023
// if this is fixed, the accept prop can be used instead of custom validation in the onDrop function
export const PoolDropzone = withStyles(styles)(
    class PoolDropzoneBase extends Component<PropsWithChildren<PoolDropzoneProps>> {
        private getClassNames = (isDragAccept: boolean, isDragActive: boolean, isDragReject: boolean): string => {
            const { className, classes } = this.props;
            if (this.props.disabled) {
                return `${classes.root} ${classes.disabled}`;
            }
            return `${classes.root} ${className || ''} ${isDragAccept ? classes.onAccept : ''} ${
                isDragReject ? classes.onReject : ''
            } ${isDragActive ? classes.onAccept : ''}`;
        };

        public override render() {
            const { classes, children, ...rest } = this.props;

            return (
                <Dropzone {...rest}>
                    {({ getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject }) => (
                        <div
                            {...getRootProps({
                                className: this.getClassNames(isDragAccept, isDragActive, isDragReject),
                            })}
                        >
                            <input {...getInputProps()} />
                            <DropzoneIcon />
                            <PoolTypography typography="body2" className={classes.caption}>
                                {children || (
                                    <FormattedMessage
                                        id={
                                            isDragActive
                                                ? 'components.dropzone.dragState'
                                                : 'components.dropzone.fileUpload'
                                        }
                                    />
                                )}
                            </PoolTypography>
                        </div>
                    )}
                </Dropzone>
            );
        }
    }
);
